import React, {
  useRef, useEffect, useState, createRef
} from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  Autoplay, Navigation, Pagination, Mousewheel
} from "swiper";
import * as style from "./style.mod.scss";
import Link from "components/link";
import RichText from "components/rich-text";
import getImagePath from "helpers/get-image-path";
import { Container } from "react-bootstrap";
import { trackTeaserClick } from "helpers/analytics";
import Logo from "../../assets/images/olavson-white.svg";
import { useMediaQuery } from "react-responsive";
import FoodboomLogo from "../../assets/images/FoodboomLogo.png";
import { useLocation } from "@reach/router";

const mobileDescriptionFoodboom = "FOODBOOM zieht in deine Küche ein! Gemeinsam mit Olavson bringen wir stylishes, hochwertiges Kochgeschirr mit ordentlich BOOM. Schnell sein lohnt sich – der Drop ist limitiert!";

const NewHeroSection = ({ data }) => {
  const [swiper, setSwiper] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const timeoutRef = useRef(null);
  const videoRefs = useRef([]);
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" });
  const location = useLocation();
  const isFoodboom = location.pathname.includes("foodboom");
  useEffect(() => {
    videoRefs.current = data?.slides?.map(() => ({
      mobile: createRef(),
      desktop: createRef(),
    }));
  }, [data?.slides]);

  const handleImageDisplay = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      handleMediaEnd();
      handleSlideChange();
    }, 7000);
  };

  const handleSlideChange = () => {
    setActiveIndex(swiper?.realIndex);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    videoRefs.current.forEach(({ mobile, desktop }, index) => {
      const videoRef = isMobile ? mobile : desktop;
      if (index !== swiper?.realIndex) {
        if (videoRef?.current) {
          videoRef.current.pause();
          videoRef.current.currentTime = 0;
          const thumbnail = videoRef.current.parentNode.querySelector(".thumbnail");
          if (thumbnail) thumbnail.style.display = "block";
        }
      }
    });
    const currentSlide = data?.slides[swiper?.realIndex];
    if (currentSlide?.mobileVideo || currentSlide?.desktopVideo) {
      const videoRef = isMobile
        ? videoRefs.current[swiper?.realIndex]?.mobile
        : videoRefs.current[swiper?.realIndex]?.desktop;
      if (videoRef?.current) {
        const thumbnail = videoRef.current.parentNode.querySelector(".thumbnail");
        videoRef.current.defaultMuted = true;
        videoRef.current.muted = true;
        if (videoRef.current.readyState >= 3) {
          videoRef.current.play().catch((error) => {
            console.warn("Failed to play video:", error.message);
          });
          if (thumbnail) thumbnail.style.display = "none";
        } else {
          videoRef.current.addEventListener("canplay", () => {
            videoRef.current.play().catch((error) => {
              console.warn("Failed to play video after canplay:", error.message);
            });
            if (thumbnail) thumbnail.style.display = "none";
          });
        }
      }
    }
  };

  const handleMediaEnd = () => {
    if (swiper) {
      swiper.slideNext();
    }
  };

  const settings = {
    onSwiper: (s) => {
      setSwiper(s);
    },
    onSlideChange: handleSlideChange,
    initialSlide: 0,
    loopAdditionalSlides: data?.slides?.length,
    loop: true,
    spaceBetween: data?.notFullWidth ? 40 : 0,
    autoplay: false,
    modules: [Navigation, Autoplay, Pagination, Mousewheel],
    slidesPerView: data?.notFullWidth ? 1.5 : 1,
    autoHeight: true,
    centeredSlides: true,
    speed: 1000,
    mousewheel: {
      enabled: true,
      forceToAxis: true,
    },
    pagination: data?.slides?.length > 1,
    navigation: data?.slides?.length > 1,
    loopFillGroupWithBlank: true,
  };

  const Slide = ({ slide, index }) => (
    <div className={style.newHeroSectionBox}>
      {slide?.badgeImage && (
        <div className={style.badgeContainer}>
          <img
            src={getImagePath(slide?.badgeImage)}
            alt={slide?.badgeImage?.alt}
            className={style.badgeIcon}
          ></img>
        </div>
      )}
      <Link
        to={slide?.button?.btnLink}
        onClick={() =>
          trackTeaserClick(slide?.button?.btnTitle, slide?.button?.btnLink)
        }
      >
        {slide?.overlay && (
          <div
            className={style.imageOverlay}
            style={data?.notFullWidth ? { borderRadius: "12px" } : null}
          ></div>
        )}
        {slide?.image || slide?.mobileImage ? (
          <div>
            <img
              style={data?.notFullWidth ? { borderRadius: "12px" } : null}
              className={
                data?.notFullWidth ? style.mobileHalfImg : style.mobileImg
              }
              src={getImagePath(slide?.mobileImage)}
              alt={slide?.mobileImage?.alt}
              onLoad={() => {
                if (activeIndex === index) {
                  handleImageDisplay();
                }
              }}
            />
            <img
              style={{
                borderRadius: data?.notFullWidth ? "12px" : null,
                height: data?.newLayout ? "calc(100vh - 42px)" : "640px",
              }}
              className={style.desktopImg}
              src={getImagePath(slide?.image)}
              alt={slide?.image?.alt}
              onLoad={() => {
                if (activeIndex === index) {
                  handleImageDisplay();
                }
              }}
            />
          </div>
        ) : (
          <div className={`${data?.newLayout && style.backgroundNewLayout}`}>
            {isMobile ?
              (<video
                ref={videoRefs?.current[index]?.mobile}
                playsInline
                autoPlay
                muted={true}
                type="video/mp4"
                poster={getImagePath(slide?.mobileVideoThumbnail)}
                style={data?.notFullWidth ? { borderRadius: "12px" } : null}
                className={
                  data?.notFullWidth ? style.mobileHalfImg : style.mobileImg
                }
                onEnded={handleMediaEnd}
                onPlay={() => {
                  const thumbnail = videoRefs.current[index]?.mobile?.current?.parentNode.querySelector(".thumbnail");
                  if (thumbnail) thumbnail.style.display = "none";
                }}
                controls={false}
                id={index}
              >
                <source src={slide.mobileVideo?.url} type="video/mp4" />
                Sorry, your browser doesn’t support embedded videos.
              </video>) : (
                <video
                  playsInline
                  autoPlay
                  muted={true}
                  type="video/mp4"
                  ref={videoRefs?.current[index]?.desktop}
                  poster={getImagePath(slide?.desktopVideoThumbnail)}
                  style={{
                    borderRadius: data?.notFullWidth ? "12px" : null,
                    height: data?.newLayout ? "calc(100vh - 42px)" : "640px",
                  }}
                  className={style.desktopImg}
                  onEnded={handleMediaEnd}
                  onPlay={() => {
                    const thumbnail = videoRefs.current[index]?.desktop?.current?.parentNode.querySelector(".thumbnail");
                    if (thumbnail) thumbnail.style.display = "none";
                  }}
                  controls={false}
                  id={index}
                >
                  <source src={slide.desktopVideo?.url} type="video/mp4" />
                  Sorry, your browser doesn’t support embedded videos.
                </video>
              )
            }
          </div>
        )}
        <Container className={data?.notFullWidth ? "small-container" : ""}>
          <div className={style.slideBox}>
            <div className={`${style.slideContent} desktop-${slide?.position}`}>
              <div
                className={`${style.slideContentPosition} mobile-${slide?.mobilePosition}`}
              >
                <div
                  className={`${style.innerContainer} desktop-alignment-${slide?.alignment
                    } mobile-alignment-${slide?.mobileAlignment} ${data?.newLayout ? style.innerNewLayout : ""
                    } ${style.swiperContentOverlay}`}
                >
                  {data?.newLayout && (
                    <>
                      {isFoodboom ? (
                        <img src={FoodboomLogo} alt="Foodboom Logo" className={style.logo} />
                      ) : (
                        <img src={Logo} alt="Logo" className={style.logo} />
                      )}
                    </>
                  )}
                  {slide?.subtitle && <h6 style={{ color: slide?.subtitleColor?.hex }}>{slide?.subtitle}</h6>}
                  {slide?.title && (
                    <h2
                      className={style.title}
                      style={{ color: slide?.headingColor?.hex }}
                    >
                      {slide?.title}
                    </h2>
                  )}
                  {(isMobile && isFoodboom && mobileDescriptionFoodboom) ? (
                    <div
                      className={`${style.descriptionBox} ${data?.newLayout ? style.landingDescriptionBox : ""}`}
                    >
                      <p>{mobileDescriptionFoodboom}</p>
                    </div>
                  ) : (
                    slide?.description && (
                      <div
                        className={`${style.descriptionBox} ${data?.newLayout ? style.landingDescriptionBox : ""}`}
                      >
                        <RichText
                          blocks={slide?.description}
                          color={slide?.headingColor?.hex}
                        />
                      </div>
                    )
                  )}
                  {slide?.button?.btnLink && slide?.button?.btnTitle && (
                    <div
                      style={{
                        border: `1px solid ${slide?.ctaBorderColor?.hex}`,
                        borderRadius: data?.newLayout ? "5px" : "8px",
                        backgroundColor: slide?.ctaBackgroundColor?.hex,
                      }}
                      className={`${style.cardButton} ${data?.newLayout ? style.cardButtonNewLayout : ""
                        }`}
                    >
                      <p style={{ color: slide?.ctaColor?.hex }}>
                        {slide?.button?.btnTitle}
                      </p>
                    </div>
                  )}
                  {slide?.usp && (
                    <div className={style.uspBox}>
                      <RichText blocks={slide?.usp} />
                    </div>
                  )}
                </div>
              </div>
            </div>
            ``
          </div>
        </Container>
      </Link>
    </div>
  );

  return (
    <div
      className={`${style.newHeroSectionSlider} ${data?.notFullWidth ? style.notFullWidthSlider : ""
        }`}
    >
      {data?.notFullWidth && (
        <div className={style.contentBox}>
          {data?.title && <h2>{data?.title}</h2>}
          {data?.description && <p>{data?.description}</p>}
        </div>
      )}
      <Swiper {...settings}>
        {data?.slides &&
          data?.slides?.length > 0 &&
          (data?.newLayout ? (
            <div className={style.newHeroSectionSlides}>
              <Slide slide={data?.slides[0]} />
            </div>
          ) : (
            data?.slides?.map((slide, i) => (
              <SwiperSlide
                key={i}
                className={style.newHeroSectionSlides}
              >
                <Slide slide={slide} index={i} />
              </SwiperSlide>
            ))
          ))}
      </Swiper>
    </div>
  );
};

export default NewHeroSection;